import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { SetupWizardService } from "@services/setup-wizard.service";
import { Subject, combineLatest } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { NbDialogRef, NbDialogService } from "@nebular/theme";
import { Renderer2, ElementRef } from "@angular/core";
import { WizardCheckToCloseModalComponent } from "../wizard-check-to-close-modal/wizard-check-to-close-modal.component";

@Component({
  selector: "app-wizard-modal",
  templateUrl: "./wizard-modal.component.html",
  styleUrls: ["./wizard-modal.component.scss"],
})
export class WizardModalComponent implements OnInit, OnDestroy, AfterViewInit {
  public destroy$: Subject<boolean> = new Subject<boolean>();
  public steps = [
    {
      label: "Integrations",
      translation_key: "integrations",
      component: "WizardIntegrations",
    },
    {
      translation_key: "edit_theme",
      label: "Edit Theme",
      component: "WizardTheme",
    },
    {
      translation_key: "invite_users",
      label: "Invite Users",
      component: "WizardUsers",
    },
    {
      // translation_key: "import_terms",
      // label: "Import Terms",
      translation_key: "import",
      label: "Import",
      component: "WizardImportComponent",
    },
  ];
  public selectedIndex: number = 0;
  public instanceData;
  public forwardButtonContent = "next";
  @ViewChild("yourElement") element: ElementRef;
  public elementsLabelIndex: any;
  public connector: any;
  public isLoading: boolean = true;
  public integrationStepState: StepState = StepState.Untouched;
  public themeStepState: StepState = StepState.Untouched;
  public usersStepState: StepState = StepState.Untouched;
  public termsStepState: StepState = StepState.Untouched;
  private dialogCheckRef: NbDialogRef<WizardCheckToCloseModalComponent>;

  constructor(
    private dialogRef: NbDialogRef<WizardModalComponent>,
    private renderer: Renderer2,
    private el: ElementRef,
    private dialog: NbDialogService,
    private wizardService: SetupWizardService
  ) {}
  ngOnInit() {
    this.wizardService.openSocket();
    this.wizardService
      .getInstanceDetails$()
      .pipe(take(1))
      .subscribe((instanceData: any) => {
        this.instanceData = instanceData;
        this.setInitialStep();
      });
    this.wizardService
      .getInstanceDetails$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((instanceData: any) => {
        this.instanceData = instanceData;
        this.forwardButtonContent = this.getForwardButtonContent();
      });
  }

  ngAfterViewInit() {
    this.getElements();
    this.getStepStates();
    this.isLoading = false
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  closeModal(shouldClose) {
    if (!shouldClose) return;
    const hasChanges = this.wizardService.checkForChangesBeforeClose();

    if (!hasChanges) {
      this.wizardService.closeModalState();
      return this.dialogRef.close();
    }

    this.dialogCheckRef = this.dialog.open(WizardCheckToCloseModalComponent, {
      dialogClass: "wizard-check-to-close-modal",
    });
    this.dialogCheckRef.onClose.subscribe((shouldClose) => {
      if (shouldClose) {
        this.wizardService.closeModalState();
        return this.dialogRef.close();
      } else {
        this.selectedIndex = hasChanges.step;
      }
    });
  }

  getElements() {
    this.elementsLabelIndex = this?.el?.nativeElement?.querySelectorAll(
      "nb-stepper.vertical > .header .step .label-index"
    );

    this.connector = this?.el?.nativeElement?.querySelectorAll(
      "nb-stepper.vertical > .header .connector"
    );
  }

  getStepStates() {
    combineLatest([
      this.wizardService.integrationInput$,
      this.wizardService.themeInput$,
      this.wizardService.usersInput$,
      this.wizardService.termsInput$,
      this.wizardService.getInstanceDetails$(),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([
          integrationState,
          themeState,
          userState,
          termsState,
          instanceDetails,
        ]: any) => {
          this.integrationStepState =
            this.selectedIndex === 0
              ? StepState.Selected
              : integrationState.hasErrors ||
                (integrationState.hasVisited &&
                  instanceDetails.wizard_integration_set === false)
              ? StepState.Error
              : integrationState.hasUpdates
              ? StepState.Selected
              : instanceDetails.wizard_integration_set !== false
              ? StepState.Completed
              : StepState.Untouched;

          this.themeStepState =
            this.selectedIndex === 1
              ? StepState.Selected
              : themeState.hasErrors ||
                (themeState.hasVisited &&
                  instanceDetails.wizard_theme_set === false)
              ? StepState.Error
              : themeState.hasUpdates
              ? StepState.Selected
              : instanceDetails.wizard_theme_set !== false
              ? StepState.Completed
              : StepState.Untouched;

          this.usersStepState =
            this.selectedIndex === 2
              ? StepState.Selected
              : userState.hasErrors ||
                (userState.hasVisited &&
                  instanceDetails.wizard_users_set === false)
              ? StepState.Error
              : userState.hasUpdates
              ? StepState.Selected
              : instanceDetails.wizard_users_set !== false
              ? StepState.Completed
              : StepState.Untouched;
          this.termsStepState =
            this.selectedIndex === 3
              ? StepState.Selected
              : termsState.hasErrors ||
                (termsState.hasVisited &&
                  instanceDetails.wizard_terms_set === false)
              ? StepState.Error
              : termsState.hasUpdates
              ? StepState.Selected
              : instanceDetails.wizard_terms_set !== false
              ? StepState.Completed
              : StepState.Untouched;

          this.setStepperColors();
        }
      );
  }

  setInitialStep() {
    if (!this.instanceData?.wizard_integration_set)
      return (this.selectedIndex = 0);
    if (!this.instanceData?.wizard_theme_set) return (this.selectedIndex = 1);
    if (!this.instanceData?.wizard_users_set) return (this.selectedIndex = 2);
    if (!this.instanceData?.wizard_terms_set) return (this.selectedIndex = 3);
  }

  updateStep($event) {
    this.selectedIndex = $event.index;
    this.forwardButtonContent = this.getForwardButtonContent();
    const previousStep =
      $event.previouslySelectedIndex === 0
        ? "integration"
        : $event.previouslySelectedIndex === 1
        ? "theme"
        : $event.previouslySelectedIndex === 2
        ? "users"
        : "terms";

    if (this.selectedIndex === 2) {
      this.wizardService.updateWizardStepVisited("theme");
    }
    if (this.selectedIndex === 3) {
      this.wizardService.updateWizardStepVisited("theme");
      this.wizardService.updateWizardStepVisited("users");
      this.wizardService.getTermsNetworkRequest$()
    }
    this.wizardService.updateWizardStepVisited(previousStep);
  }

  async setStepperColors() {
    const selectedOrCompleted = "#2ea3f2";
    const untouched = "#e4e8ed";
    const error = "#c24848";
    const errorBackground = "#fbf0f0";
    const defaultBackground = "#fff";

    this.connector?.forEach((element, i) => {
      if (i === 0) {
        const color =
          this.selectedIndex === 0
            ? untouched
            : this.integrationStepState === StepState.Error
            ? error
            : selectedOrCompleted;
        this.renderer.setStyle(element, "background", color);
      }
      if (i === 1) {
        const color =
          this.selectedIndex < 2
            ? untouched
            : this.themeStepState === StepState.Error
            ? error
            : selectedOrCompleted;
        this.renderer.setStyle(element, "background", color);
      }
      if (i === 2) {
        const color =
          this.selectedIndex < 3
            ? untouched
            : this.usersStepState === StepState.Error
            ? error
            : selectedOrCompleted;
        this.renderer.setStyle(element, "background", color);
      }
    });

    this.elementsLabelIndex?.forEach((element, i) => {
      if (i === 0) {
        let color;
        if (
          this.integrationStepState === StepState.Completed ||
          StepState.Selected
        ) {
          color = selectedOrCompleted;
        }
        if (this.integrationStepState === StepState.Error) {
          color = error;
        }
        if (this.integrationStepState === StepState.Untouched) {
          color = untouched;
        }
        const background =
          this.integrationStepState === StepState.Error
            ? errorBackground
            : this.integrationStepState === StepState.Completed
            ? selectedOrCompleted
            : defaultBackground;
        this.renderer.setStyle(element, "color", color);
        this.renderer.setStyle(element, "border-color", color);
        this.renderer.setStyle(element, "background-color", background);
      }
      if (i === 1) {
        let color;
        if (this.themeStepState === StepState.Completed || StepState.Selected) {
          color = selectedOrCompleted;
        }
        if (this.themeStepState === StepState.Error) {
          color = error;
        }
        if (this.themeStepState === StepState.Untouched) {
          color = untouched;
        }
        const background =
          this.themeStepState === StepState.Error
            ? errorBackground
            : this.themeStepState === StepState.Completed
            ? selectedOrCompleted
            : defaultBackground;
        this.renderer.setStyle(element, "color", color);
        this.renderer.setStyle(element, "border-color", color);
        this.renderer.setStyle(element, "background-color", background);
      }
      if (i === 2) {
        let color;
        if (this.usersStepState === StepState.Completed || StepState.Selected) {
          color = selectedOrCompleted;
        }
        if (this.usersStepState === StepState.Error) {
          color = error;
        }
        if (this.usersStepState === StepState.Untouched) {
          color = untouched;
        }
        const background =
          this.usersStepState === StepState.Error
            ? errorBackground
            : this.usersStepState === StepState.Completed
            ? selectedOrCompleted
            : defaultBackground;
        this.renderer.setStyle(element, "color", color);
        this.renderer.setStyle(element, "border-color", color);
        this.renderer.setStyle(element, "background-color", background);
      }
      if (i === 3) {
        let color;
        if (this.termsStepState === StepState.Completed || StepState.Selected) {
          color = selectedOrCompleted;
        }
        if (this.termsStepState === StepState.Error) {
          color = error;
        }
        if (this.termsStepState === StepState.Untouched) {
          color = untouched;
        }
        const background =
          this.termsStepState === StepState.Error
            ? errorBackground
            : this.termsStepState === StepState.Completed
            ? selectedOrCompleted
            : defaultBackground;
        this.renderer.setStyle(element, "color", color);
        this.renderer.setStyle(element, "border-color", color);
        this.renderer.setStyle(element, "background-color", background);
      }
    });
  }

  getForwardButtonContent() {
    if (this.selectedIndex === 0) {
      return this.instanceData.wizard_integration_set === true ||
        !this.instanceData.hasOwnProperty("wizard_integration_set")
        ? "next"
        : "skip";
    }
    if (this.selectedIndex === 1) {
      return this.instanceData.wizard_theme_set === true ||
        !this.instanceData.hasOwnProperty("wizard_theme_set")
        ? "next"
        : "skip";
    }
    if (this.selectedIndex === 2) {
      return this.instanceData.wizard_users_set === true ||
        !this.instanceData.hasOwnProperty("wizard_users_set")
        ? "next"
        : "skip";
    }
    if (this.selectedIndex === 3) {
      return this.instanceData.wizard_users_set === true ||
        !this.instanceData.hasOwnProperty("wizard_terms_set")
        ? "finish"
        : "skip";
    }
    return "next";
  }

  indirectTasksHasCompleted(index) {
      if (index === 0) {
        return this.integrationStepState === StepState.Completed;
      }
      if (index === 1) {
        return this.themeStepState === StepState.Completed;
      }
      if (index === 2) {
        return this.usersStepState === StepState.Completed;
      }
      if (index === 3) {
        return this.termsStepState === StepState.Completed;
      }
  }
}

enum StepState {
  Error = "error",
  Untouched = "untouched",
  Selected = "selected",
  Completed = "completed",
  hasUpdates = "hasupdates",
}
